import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, AlertTriangle, FileWarning, Hourglass, UserPlus, TrendingUp, CheckSquare } from "lucide-react";
import { api, fmtEuro, fmtDate } from "@/lib/api";
import { useSyncedReload } from "@/hooks/useSyncedReload";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { RefBadge } from "@/components/shared";

const Card = ({ title, icon: Icon, children, testId, className = "" }) => (
  <div data-testid={testId} className={`bg-white border border-stone-200 rounded-xl p-6 hover:border-stone-300 transition-all duration-200 ${className}`}>
    <div className="flex items-center gap-2.5 mb-4">
      <Icon size={17} strokeWidth={1.5} className="text-[#9F8E7E]" />
      <h3 className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">{title}</h3>
    </div>
    {children}
  </div>
);

const Empty = ({ text }) => <p className="text-sm text-stone-400">{text}</p>;

const QuickAdd = ({ to, icon: Icon, label, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="inline-flex items-center gap-1.5 bg-white border border-stone-200 hover:border-stone-500 hover:shadow-sm rounded-full px-3 py-1.5 text-xs font-medium text-stone-700 transition-all"
  >
    <Icon size={13} strokeWidth={1.75} className="text-[#9F8E7E]" />
    {label}
  </Link>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const { t, lang } = useLang();
  const { user } = useAuth();

  const load = useCallback(() => {
    api.get("/dashboard").then((res) => setData(res.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useSyncedReload(load);

  if (!data) {
    return <div className="py-24 text-center text-stone-400 text-sm animate-pulse">…</div>;
  }

  const greeting = lang === "fr" ? "Bonjour" : "Good day";

  return (
    <div data-testid="dashboard-page">
      <div className="mb-10">
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-stone-900">
          {greeting}, {user?.name?.split(" ")[0] || "Julia"}
        </h1>
        <p className="text-sm text-stone-500 mt-2">
          {new Date().toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="dashboard-quick-add" hidden>
        <QuickAdd to="/diary" icon={CalendarClock} label={t("qaNote")} testId="quick-add-note-dash" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        <Card title={t("commissionPipeline")} icon={TrendingUp} testId="dashboard-pipeline-card" className="md:col-span-2 lg:col-span-1">
          <div className="flex items-end gap-8">
            <div>
              <p className="text-3xl font-medium tracking-tight text-stone-900">{fmtEuro(data.pipeline.live)}</p>
              <p className="text-xs text-stone-400 mt-1">{t("live")}</p>
            </div>
            <div>
              <p className="text-3xl font-medium tracking-tight text-[#3F6212]">{fmtEuro(data.pipeline.under_offer)}</p>
              <p className="text-xs text-stone-400 mt-1">{t("underOffer")}</p>
            </div>
          </div>
        </Card>

        <Card title={t("todaysAppointments")} icon={CalendarClock} testId="dashboard-appointments-card">
          {data.todays_viewings.length === 0 ? <Empty text={t("nothingToday")} /> : (
            <ul className="space-y-3">
              {data.todays_viewings.map((v) => (
                <li key={v.id} className="text-sm">
                  <Link to="/viewings" className="font-medium text-stone-800 hover:text-slate-900">
                    {v.date_time?.slice(11, 16)} — {v.buyer_name}
                  </Link>
                  <p className="text-xs text-stone-400">{v.property_name}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title={t("upcomingTasks")} icon={CheckSquare} testId="dashboard-tasks-card">
          {(!data.upcoming_tasks || data.upcoming_tasks.length === 0) ? <Empty text={t("noPendingTasks")} /> : (
            <ul className="space-y-3" data-testid="dashboard-tasks-list">
              {data.upcoming_tasks.slice(0, 6).map((task) => {
                const overdue = task.due_date && task.due_date < new Date().toISOString().slice(0, 10);
                return (
                  <li key={task.id} className="text-sm" data-testid={`dashboard-task-${task.id}`}>
                    <Link to="/tasks" className="font-medium text-stone-800 hover:text-slate-900 flex items-start gap-2">
                      {task.priority === "high" && <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-[#9A3412]" aria-label="high priority" />}
                      <span className="flex-1">{task.title}</span>
                    </Link>
                    <p className={`text-xs mt-0.5 ${overdue ? "text-[#9A3412]" : "text-stone-400"}`}>
                      {task.due_date ? fmtDate(task.due_date, lang) : t("noDueDate")}
                      {task.linked_name ? ` · ${task.linked_name}` : ""}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
          <Link to="/tasks" data-testid="dashboard-view-all-tasks" className="mt-4 inline-block text-xs font-medium text-stone-500 hover:text-slate-900 transition-colors">
            {t("viewAllTasks")} →
          </Link>
        </Card>

        <Card title={t("urgentFollowUps")} icon={AlertTriangle} testId="dashboard-urgent-card">
          {data.urgent_tasks.length === 0 ? <Empty text={t("allClear")} /> : (
            <ul className="space-y-3">
              {data.urgent_tasks.slice(0, 5).map((task) => {
                // Show the most recent progress note (last non-empty line of the task's notes)
                const lastNote = (task.notes || "").split("\n").map((s) => s.trim()).filter(Boolean).pop();
                return (
                  <li key={task.id} className="text-sm">
                    <Link to="/tasks" className="font-medium text-stone-800 hover:text-slate-900">{task.title}</Link>
                    <p className="text-xs text-[#9A3412]">{fmtDate(task.due_date, lang)}</p>
                    {lastNote && (
                      <p data-testid={`urgent-note-${task.id}`} className="text-xs text-stone-500 mt-0.5 line-clamp-2 italic">
                        {lastNote}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card title={t("missingDocuments")} icon={FileWarning} testId="dashboard-docs-card">
          {data.missing_docs.length === 0 ? <Empty text={t("allClear")} /> : (
            <ul className="space-y-3">
              {data.missing_docs.slice(0, 4).map((s) => (
                <li key={s.id} className="text-sm">
                  <Link to="/sellers" className="font-medium text-stone-800 hover:text-slate-900 inline-flex items-center gap-2">
                    <RefBadge code={s.ref_code} /> {s.name}
                  </Link>
                  <p className="text-xs text-stone-400">{s.missing.slice(0, 4).map((m) => m.replace(/_/g, " ")).join(", ")}{s.missing.length > 4 ? "…" : ""}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title={t("upcomingDecisions")} icon={Hourglass} testId="dashboard-decisions-card">
          {data.upcoming_decisions.length === 0 ? <Empty text={t("allClear")} /> : (
            <ul className="space-y-3">
              {data.upcoming_decisions.map((v) => (
                <li key={v.id} className="text-sm">
                  <Link to="/viewings" className="font-medium text-stone-800 hover:text-slate-900">{v.buyer_name}</Link>
                  <p className="text-xs text-stone-400">{v.property_name} · {t("decisionBy")} {fmtDate(v.decision_date, lang)}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title={t("newEnquiries")} icon={UserPlus} testId="dashboard-enquiries-card">
          {data.new_buyers.length === 0 ? <Empty text={t("noResults")} /> : (
            <ul className="space-y-3">
              {data.new_buyers.slice(0, 4).map((b) => (
                <li key={b.id} className="text-sm">
                  <Link to="/buyers" className="font-medium text-stone-800 hover:text-slate-900 inline-flex items-center gap-2">
                    <RefBadge code={b.ref_code} /> {b.name}
                  </Link>
                  <p className="text-xs text-stone-400">{b.country} · {fmtEuro(b.budget_min)} – {fmtEuro(b.budget_max)}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
